import { useEffect, useState } from 'react'
import { can } from '../../lib/api'
import type { ExitChain, ExitSummary, VendorExitRecord } from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { useSave } from '../../lib/save'
import { ErrorBox } from '../DataTable'
import { proposalFooter } from '../footers'
import { LifecycleControl } from '../LifecycleControl'
import { Dialog, NextUp, StageTrack, Status, Toggletip, type Stage } from '../kit'
import { Avatar } from '../ui'
import { chainStates, decidable, signRefusal, type Signing } from './ExitChainPanel'
import { TRIGGER_WORDS, dayMonth, dayMonthYear, openFor, same, shortPerson, stageHelp, stagePosition, truncate } from './words'

// The wide panel at the top of the exit workspace: the six stages as a track,
// the next person in one bar under it with the time the stage has left, and
// the move that closes the exit.
//
// # THREE SHAPES, ONE FRAME
//
// A proposal has not started, so its track is the chain it would be held to,
// greyed, and the bar under it is the decision: start the exit or dismiss it,
// in the same frame as the chain the decision commits people to. An exit under
// way is the chain as a live track with the next office in the bar. A vendor
// with no exit shows the chain it would be held to today with the way to begin.
//
// # NOTHING ESTIMATED
//
// The time-left bar is drawn only where GET /v1/exits returned the plan's
// length. An exit with no plan says how long it has been open and no more.

// The chain as the track draws it: who signed and when under a signed stage,
// the due date under the stage the exit has reached, "final" under the last.
export function chainTrack(
  chain: ExitChain,
  summary: ExitSummary | null,
  underWay: boolean,
  label: (s: string | undefined) => string,
): Stage[] {
  const blocked = !!summary?.blocker && !summary.blocker.startsWith('Declined')
  const at = stagePosition(chain.stages)
  const states = chainStates(chain, underWay, blocked)
  const last = chain.stages.length - 1
  return chain.stages.map((s, i) => {
    const decided = s.state !== 'outstanding'
    const isAt = underWay && i === at
    let sub: string | undefined
    let meta: string | undefined
    if (decided) {
      sub = shortPerson(label(s.signedBy))
      meta = dayMonth(s.signedAt)
    } else if (isAt) {
      sub = blocked ? 'blocked' : 'with them now'
      meta = summary?.dueOn ? 'due ' + dayMonth(summary.dueOn) : undefined
    } else if (i === last) {
      meta = 'final'
    }
    return { key: s.key, label: s.job, state: states[i] ?? 'upcoming', sub, meta }
  })
}

export function ExitStagePanel({
  vendorId,
  status,
  chain,
  summary,
  signable,
  subject,
  announce,
  onSign,
  onChanged,
}: {
  vendorId: string
  status: string
  chain: ExitChain
  summary: ExitSummary | null
  signable: boolean
  subject: string
  announce: string
  onSign: (s: Signing) => void
  onChanged: () => void
}) {
  const names = usePeopleNames()
  const label = (s: string | undefined) => personLabel(names, s)
  const exit = chain.exit
  const proposal = exit?.status === 'PROPOSED' ? exit : null
  const underWay = status === 'OFFBOARDING'
  const inRegister = status === 'ACTIVE' || status === 'SUSPENDED'
  const mayRun = can('offboarding.run')

  const at = stagePosition(chain.stages)
  const current = underWay ? chain.stages[at] : undefined
  const daysOpen = summary?.daysOpen
  const planned = summary?.plannedDays
  const mayActNow = current && decidable(current, signable, subject) && signRefusal(chain, current, subject) === ''
  const preview = inRegister || !!proposal
  const openLine = !planned && daysOpen !== undefined
  const nextAction =
    openLine || mayActNow ? (
      <>
        {openLine && (
          <span className="exit-next-open">
            <b>{openFor(daysOpen, undefined)}</b>
            <small>No plan end date</small>
          </span>
        )}
        {current && mayActNow && (
          <button type="button" className="primary" onClick={() => onSign({ stage: current, decision: 'SIGN' })}>
            Sign
          </button>
        )}
      </>
    ) : undefined

  return (
    <section className="panel exit-stage" aria-label="Where offboarding is">
      {chain.total > 0 ? (
        <div className={'exit-track' + (preview ? ' exit-track-preview' : '')}>
          <StageTrack stages={chainTrack(chain, summary, underWay, label)} label={'Offboarding sign-off, ' + chain.total + ' stages'} announce={announce} />
        </div>
      ) : (
        <p className="exit-sub">No offboarding chain applies at this tier; the approval matrix needs an offboarding row</p>
      )}

      {proposal && <ProposalDecision exit={proposal} subject={subject} total={chain.total} onDone={onChanged} />}

      {!proposal && inRegister && chain.total > 0 && (
        <p className="exit-sub exit-center">The chain offboarding would be held to today</p>
      )}

      {current && (
        <NextUp
          who={current.job}
          role={'stage ' + (at + 1) + ' of ' + chain.total}
          what={stageHelp(current.key)}
          due={summary?.dueOn ? dayMonth(summary.dueOn) : ''}
          progress={
            planned && daysOpen !== undefined
              ? { value: (daysOpen / planned) * 100, label: openFor(daysOpen, planned) }
              : undefined
          }
          action={nextAction}
        />
      )}

      {underWay && !current && chain.total > 0 && (
        <p className="exit-done-line">
          <Status tone="ok">All signed</Status>
          <span className="exit-sub">Close offboarding once the evidence is in</span>
        </p>
      )}

      {!proposal && mayRun && (inRegister || underWay) && (
        <div className="exit-moves">
          <LifecycleControl vendorId={vendorId} only={['offboard', 'offboarded']} onChanged={onChanged} />
        </div>
      )}
    </section>
  )
}

// ------------------------------------------------------------ the proposal

function ProposalDecision({ exit, subject, total, onDone }: { exit: VendorExitRecord; subject: string; total: number; onDone: () => void }) {
  const names = usePeopleNames()
  const [acting, setActing] = useState<'confirm' | 'dismiss' | null>(null)
  const mayRun = can('offboarding.run')
  const proposedByMe = subject !== '' && exit.proposedBy !== undefined && same(exit.proposedBy, subject)
  const detail = exit.triggerDetail ?? {}
  const worker = exit.proposedBy === 'worker'
  const by = worker ? 'Raised automatically' : 'Proposed by ' + shortPerson(personLabel(names, exit.proposedBy))
  const trigger = TRIGGER_WORDS[exit.triggerKind] ?? exit.triggerKind

  return (
    <div className="exit-decision" role="region" aria-label="Offboarding proposal">
      <div className="exit-decision-who">
        <Avatar name={worker ? 'Worker' : personLabel(names, exit.proposedBy) || 'Proposal'} size={30} />
        <span className="exit-decision-text">
          <span>
            <b>Proposed: {trigger}.</b> {total > 0 ? total + ' sign-offs follow if it starts' : 'Nothing is signed until it starts'}
          </span>
          <span className="exit-sub">
            {by}
            {exit.proposedAt ? ', ' + dayMonthYear(exit.proposedAt) : ''}
            {typeof detail.reference === 'string' ? ', ' + String(detail.regulator ?? '') + ' ' + detail.reference : ''}
          </span>
          {exit.reason && (
            <span className="exit-quote" title={exit.reason}>
              {truncate(exit.reason, 80)}
            </span>
          )}
        </span>
      </div>
      {mayRun ? (
        <div className="exit-decision-act">
          <button type="button" className="primary" disabled={proposedByMe} onClick={() => setActing('confirm')}>
            Start offboarding
          </button>
          <button type="button" onClick={() => setActing('dismiss')}>
            Dismiss
          </button>
          <Toggletip label="Who may start it">
            Whoever proposed offboarding cannot start it. A proposal the product raised can be started by anybody who runs
            offboarding.
          </Toggletip>
          {proposedByMe && <span className="exit-sub">You proposed this, so somebody else starts it</span>}
        </div>
      ) : (
        <span className="exit-sub">Somebody who runs offboarding decides it</span>
      )}
      <DecideProposalDrawer
        exit={exit}
        acting={acting}
        onClose={() => setActing(null)}
        onDone={() => {
          setActing(null)
          onDone()
        }}
      />
    </div>
  )
}

function DecideProposalDrawer({
  exit,
  acting,
  onClose,
  onDone,
}: {
  exit: VendorExitRecord
  acting: 'confirm' | 'dismiss' | null
  onClose: () => void
  onDone: () => void
}) {
  const [kept, setKept] = useState<'confirm' | 'dismiss'>('confirm')
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<unknown>(null)

  useEffect(() => {
    if (!acting) return
    setKept(acting)
    setReason('')
    setErr(null)
  }, [acting])

  const dismiss = kept === 'dismiss'
  const id = exit.id ?? ''
  const title = dismiss ? 'Dismiss the proposal' : 'Start offboarding'
  const save = useSave(acting, title)



  return (
    <Dialog
      shape="modal"
      kind="form"
      open={acting !== null}
      onClose={onClose}
      title={title}
      {...proposalFooter({ exitId: id, act: kept, reason, busy: save.busy }, (work) => {
        setErr(null)
        save.send(work, { done: onDone, failed: setErr })
      })}
    >
      <div className="exit-drawer-stack">
        <p className="exit-para">
          {dismiss
            ? 'The same fact will not propose offboarding again.'
            : 'The vendor moves to offboarding and its sign-off chain starts.'}
        </p>
        {exit.reason && (
          <div>
            <h3 className="exit-h3">Why it was proposed</h3>
            <p className="exit-para">{exit.reason}</p>
          </div>
        )}
        {err != null && <ErrorBox error={err} what="The proposal" verb="decided" />}
        <div className="field">
          <label htmlFor="proposal-reason">{dismiss ? 'Why it is not pursued' : 'Reason, optional'}</label>
          <textarea id="proposal-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          {!dismiss && <p className="field-note">Left empty, the proposal's own reason is used</p>}
        </div>
      </div>
    </Dialog>
  )
}
